import { useEffect, useState } from "react";

import { Constants } from "src/utils/constants";

type DebouncePayload<T> = [T, T, (val: T) => void];

/**
 * Debounces a value by delaying updates until after the specified delay
 * Self-contained hook that manages its own state and returns both immediate and debounced values.
 *
 * @param initialValue - The initial value
 * @param delay - Delay in milliseconds (default: Constants.DEBOUNCE_DEFAULT = 350ms)
 * @returns [value, debouncedValue, setValue]
 */
export const useDebounce = <T>(
  initialValue: T,
  delay = Constants.DEBOUNCE_DEFAULT
): DebouncePayload<T> => {
  const [value, setValue] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return [value, debouncedValue, setValue];
};
